import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { LsqDataSource } from '@lsq-frontend/angular-shared/api-service';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import {
    ArInsightsReportType,
    GetAccountSnapshotRequest,
    GetAccountSnapshotResponse,
    GetAccountSnapshotService,
    GetAgingOverviewRequest,
    GetAgingOverviewResponse,
    GetAgingOverviewService,
    GetCanDownloadInsightsReportRequest,
    GetCanDownloadInsightsReportResponse,
    GetCanDownloadInsightsReportService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    Color,
    forLifeOf,
    LifecycleAware,
    Styling,
    TourHelperService,
    ClaimService,
    FeatureFlagService,
    Icons,
    ResponsiveService
} from '@lsq-frontend/common-ui';
import { startOfMonth } from 'date-fns';
import { PopoverPlacement } from 'ngx-smart-popover';
import { combineLatest } from 'rxjs';
import { delay, filter, first, map } from 'rxjs/operators';
import { TourAnalyticsNames, TourIds, TourSteps } from '../../constants/tours.constants';

@LifecycleAware()
@Component({
    selector: 'factoring-dashboard',
    templateUrl: './factoring-dashboard.component.html',
    styleUrls: ['./factoring-dashboard.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class FactoringDashboardComponent implements OnInit, OnDestroy {
    public isMobile = false;
    public isValidClientForInsightReports = false;
    public accountSnapshotDataSource = new LsqDataSource<GetAccountSnapshotResponse>({
        data$: this._getAccountSnapshotService.dataStream,
        loading$: this._getAccountSnapshotService.isInFlightStream,
        error$: this._getAccountSnapshotService.errorStream
    });
    public agingOverviewDataSource = new LsqDataSource<GetAgingOverviewResponse>({
        data$: this._getAgingOverviewService.dataStream,
        loading$: this._getAgingOverviewService.isInFlightStream,
        error$: this._getAgingOverviewService.errorStream
    });
    public canDownloadInsightsReportDataSource = new LsqDataSource<
        GetCanDownloadInsightsReportResponse
    >({
        data$: this._getCanDownloadInsightsReportService.dataStream,
        loading$: this._getCanDownloadInsightsReportService.isInFlightStream,
        error$: this._getCanDownloadInsightsReportService.errorStream
    });
    public claim = DashboardClaim;
    public color = Color;
    public placement = PopoverPlacement;
    public startOfMonth = startOfMonth;
    public icons = Icons;

    constructor(
        private _getAccountSnapshotService: GetAccountSnapshotService,
        private _getAgingOverviewService: GetAgingOverviewService,
        private _featureFlagsService: FeatureFlagService,
        private _claimService: ClaimService,
        private _responsiveService: ResponsiveService,
        private _router: Router,
        private _tourHelperService: TourHelperService,
        private _getCanDownloadInsightsReportService: GetCanDownloadInsightsReportService,
        private _userService: UserService
    ) {}

    public ngOnInit(): void {
        // After both services return and we get the feature flag
        combineLatest(
            this._featureFlagsService.isOn(FeatureFlags.GuidedTour),
            this._responsiveService.smBelow,
            this._claimService.isOn(DashboardClaim.ViewReports),
            this._claimService.isOn(DashboardClaim.ViewRequestFunds),
            this._getAccountSnapshotService.dataStream,
            this._getAgingOverviewService.dataStream
        )
            .pipe(
                filter(([featureFlag, isMobile]) => featureFlag && !isMobile),
                first(),
                map(([_featureFlag, _isMobile, canViewReports, canViewRequestFunds]) => {
                    const tourSteps = TourSteps.createDashboardTourSteps(
                        canViewReports,
                        canViewRequestFunds
                    );
                    return this._tourHelperService.attachEndEvents(
                        {
                            tourId: TourIds.DashboardIntro,
                            minimumScreenSize: Styling.mdStart,
                            steps: tourSteps
                        },
                        TourAnalyticsNames.DashboardIntro
                    );
                }),
                delay(0),
                forLifeOf(this)
            )
            .subscribe((dashboardTour) => this._tourHelperService.startTour(dashboardTour));

        this._responsiveService.smBelow.pipe(forLifeOf(this)).subscribe((isMobile) => {
            this.isMobile = isMobile;
        });

        this._getAccountSnapshotService.execute(new GetAccountSnapshotRequest());
        this._getAgingOverviewService.execute(new GetAgingOverviewRequest());

        this._getCanDownloadInsightsReportService.execute(
            new GetCanDownloadInsightsReportRequest({
                companyKey: this._getCompanyKey(),
                reportType: ArInsightsReportType.Portfolio
            })
        );
        this.canDownloadInsightsReportDataSource.data$
            .pipe(forLifeOf(this))
            .subscribe((resp) => (this.isValidClientForInsightReports = resp.isValidClient));
    }

    public creditReportFeatureFlag$ = this._featureFlagsService.isOn(
        FeatureFlags.ArInsightsReportDownloads
    );

    public ngOnDestroy(): void {}

    private _getCompanyKey(): string {
        return this._userService.getCompanyKey();
    }
}
